// ---------------------------------------------------------------------------
// Notificaciones push (Web Push) — alta y baja de dispositivos.
//
// Dos audiencias:
//   - admin:   el comercio. El dispositivo queda atado al usuario logueado y
//              recibe pedidos nuevos y stock crítico de su empresa.
//   - cliente: el comprador de la tienda. Sin login: el dispositivo queda
//              atado a UN pedido (por su código de seguimiento).
//
// El envío lo hace la base (push_outbox) + la Edge Function push-worker; el
// service worker muestra el aviso (public/push-sw.js).
// Spec: desarrollo/spec-notificaciones-push-tienda.md (proyecto Impulza).
// ---------------------------------------------------------------------------
import { supabase } from '../supabase';

const SW_TIMEOUT_MS = 8000;
let clavePublicaCache = null;

export function soportaPush() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export function esIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPadOS se presenta como Mac: se distingue por la pantalla táctil.
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

export function esAppInstalada() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone === true;
}

/**
 * Estado del dispositivo frente a los avisos.
 * - estado 'no_soportado': navegador sin Web Push.
 * - estado 'requiere_instalar': iPhone/iPad sin la app en la pantalla de inicio
 *   (iOS solo permite push a sitios instalados, desde iOS 16.4).
 * - estado 'bloqueado': el usuario negó el permiso; solo se revierte desde
 *   la configuración del navegador.
 * - estado 'disponible': se puede pedir permiso / suscribir.
 */
export function diagnosticoPush() {
  if (esIOS() && !esAppInstalada()) return { estado: 'requiere_instalar' };
  if (!soportaPush()) return { estado: 'no_soportado' };
  if (Notification.permission === 'denied') return { estado: 'bloqueado' };
  return { estado: 'disponible', permiso: Notification.permission };
}

function base64UrlABytes(base64Url) {
  const relleno = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + relleno).replace(/-/g, '+').replace(/_/g, '/');
  const crudo = atob(base64);
  return Uint8Array.from(crudo, (c) => c.charCodeAt(0));
}

function mismaClave(a, b) {
  if (!a || !b) return false;
  const x = new Uint8Array(a);
  if (x.length !== b.length) return false;
  return x.every((v, i) => v === b[i]);
}

async function clavePublica() {
  if (clavePublicaCache) return clavePublicaCache;
  const { data, error } = await supabase.rpc('push_public_key');
  if (error || !data) throw new Error('Los avisos todavía no están configurados en el servidor.');
  clavePublicaCache = data;
  return data;
}

async function registroSW(timeoutMs = SW_TIMEOUT_MS) {
  if (!import.meta.env.PROD) {
    throw new Error('Los avisos push solo funcionan en la versión publicada (el service worker no corre en desarrollo).');
  }
  const listo = navigator.serviceWorker.ready;
  const limite = new Promise((_, rej) => setTimeout(
    () => rej(new Error('El navegador no terminó de preparar la app. Recargá la página e intentá de nuevo.')),
    timeoutMs,
  ));
  return Promise.race([listo, limite]);
}

/** Suscripción actual de este navegador (o null). No pide permiso. */
export async function suscripcionActual(timeoutMs) {
  if (!soportaPush() || !import.meta.env.PROD) return null;
  try {
    const reg = await registroSW(timeoutMs);
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Pide permiso y suscribe el navegador. Llamar SIEMPRE desde un click: iOS y
 * Safari rechazan el pedido de permiso si no viene de un gesto del usuario.
 */
async function suscribirNavegador() {
  const diag = diagnosticoPush();
  if (diag.estado === 'requiere_instalar') {
    throw new Error('En iPhone/iPad primero agregá esta página a la pantalla de inicio (Compartir → Agregar a inicio) y abrila desde ahí.');
  }
  if (diag.estado === 'no_soportado') throw new Error('Este navegador no admite notificaciones.');
  if (diag.estado === 'bloqueado') {
    throw new Error('Las notificaciones están bloqueadas para este sitio. Habilitalas desde el candado de la barra de direcciones.');
  }

  // Primero el permiso, antes de cualquier await de red, para no perder el gesto.
  const permiso = Notification.permission === 'granted'
    ? 'granted'
    : await Notification.requestPermission();
  if (permiso !== 'granted') throw new Error('No se otorgó el permiso de notificaciones.');

  const [reg, clave] = await Promise.all([registroSW(), clavePublica()]);
  const claveBytes = base64UrlABytes(clave);

  let sub = await reg.pushManager.getSubscription();
  // Si la suscripción existente se hizo con otra clave (rotación), se rehace.
  if (sub && !mismaClave(sub.options?.applicationServerKey, claveBytes)) {
    await sub.unsubscribe().catch(() => {});
    sub = null;
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: claveBytes });
  }

  const json = sub.toJSON();
  return {
    endpoint: json.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
    userAgent: navigator.userAgent,
  };
}

// --- Comercio (admin) --------------------------------------------------------

export async function activarAvisosAdmin() {
  const s = await suscribirNavegador();
  const { error } = await supabase.rpc('push_registrar_admin', {
    p_endpoint: s.endpoint, p_p256dh: s.p256dh, p_auth: s.auth, p_user_agent: s.userAgent,
  });
  if (error) throw new Error(error.message || 'No se pudo registrar el dispositivo.');
  return s.endpoint;
}

/**
 * Da de baja ESTE dispositivo como receptor del comercio. No desuscribe el
 * navegador: puede estar siguiendo pedidos como cliente con el mismo endpoint.
 * Se usa al cerrar sesión (si no, el próximo usuario del equipo recibiría los
 * pedidos de esta empresa). Nunca tira: el logout no puede trabarse por esto.
 */
export async function desactivarAvisosAdminEsteDispositivo() {
  const baja = (async () => {
    const sub = await suscripcionActual(2000);
    if (!sub) return;
    await supabase.rpc('push_desuscribir', { p_endpoint: sub.endpoint });
  })();
  // Tope total de 3 s: el cierre de sesión no puede quedar colgado por esto
  // (service worker bloqueado, red lenta). Si no llega, el endpoint queda
  // registrado hasta que el usuario lo quite desde Tienda → Notificaciones.
  const tope = new Promise((resolve) => { setTimeout(resolve, 3000); });
  try {
    await Promise.race([baja, tope]);
  } catch (e) {
    console.warn('No se pudo dar de baja el dispositivo de los avisos:', e);
  }
  baja.catch(() => {});
}

/** Dispositivos del usuario logueado (RLS: solo los propios). */
export async function listarDispositivosAdmin() {
  const { data, error } = await supabase
    .from('push_subscriptions')
    .select('id, endpoint, user_agent, created_at, last_success_at')
    .eq('audience', 'admin')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/** Encola un aviso de prueba para ESTE dispositivo (tiene que estar activado). */
export async function probarAvisosEsteDispositivo() {
  const sub = await suscripcionActual();
  if (!sub) throw new Error('Este dispositivo no tiene los avisos activados.');
  const { error } = await supabase.rpc('push_prueba', { p_endpoint: sub.endpoint });
  if (error) throw new Error(error.message || 'No se pudo enviar la prueba.');
}

export async function quitarDispositivoAdmin(id) {
  const { error } = await supabase.from('push_subscriptions').delete().eq('id', id);
  if (error) throw error;
}

// --- Cliente de la tienda ----------------------------------------------------

export async function seguirPedido(trackingCode) {
  const s = await suscribirNavegador();
  const { data, error } = await supabase.rpc('push_suscribir_pedido', {
    p_tracking_code: trackingCode, p_endpoint: s.endpoint, p_p256dh: s.p256dh, p_auth: s.auth, p_user_agent: s.userAgent,
  });
  if (error) throw new Error(error.message || 'No se pudieron activar los avisos del pedido.');
  return data;
}

export async function dejarDeSeguirPedido(trackingCode) {
  const sub = await suscripcionActual();
  if (!sub) return;
  const { error } = await supabase.rpc('push_desuscribir', { p_endpoint: sub.endpoint, p_tracking_code: trackingCode });
  if (error) throw error;
}

/** El pedido admite avisos: canal web y no cerrado (Tienda Nube avisa por su cuenta). */
export async function pedidoAdmiteAvisos(trackingCode) {
  const { data, error } = await supabase.rpc('push_pedido_admite', { p_tracking_code: trackingCode });
  if (error) return false;
  return data === true;
}

export async function sigoPedido(trackingCode) {
  const sub = await suscripcionActual();
  if (!sub || Notification.permission !== 'granted') return false;
  const { data } = await supabase.rpc('push_estado_pedido', { p_tracking_code: trackingCode, p_endpoint: sub.endpoint });
  return data === true;
}

// --- Presentación ------------------------------------------------------------

export function describirDispositivo(userAgent = '') {
  const ua = userAgent || '';
  const so = /iPhone/.test(ua) ? 'iPhone'
    : /iPad/.test(ua) ? 'iPad'
      : /Android/.test(ua) ? (/Mobile/.test(ua) ? 'Celular Android' : 'Tablet Android')
        : /Macintosh/.test(ua) ? 'Mac'
          : /Windows/.test(ua) ? 'Windows'
            : /Linux/.test(ua) ? 'Linux' : 'Dispositivo';
  const nav = /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
      : /SamsungBrowser/.test(ua) ? 'Samsung Internet'
        : /Firefox\//.test(ua) ? 'Firefox'
          : /Chrome\//.test(ua) ? 'Chrome'
            : /Safari\//.test(ua) ? 'Safari' : '';
  return nav ? `${nav} en ${so}` : so;
}
